import { FC } from "react";
import LocationInput from "../LocationInput";
import RentalCarDatesRangeInput from "./RentalCarDatesRangeInput";
import RentalCarTimesRangeInput from "./RentalCarTimesRangeInput";
import DropOffLocationInput from "../DropOffLocationInput";
import { useData } from "data/data-provider";
import SearchButton from "components/SearchButton/SearchButton";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export interface RentalCarSearchFormProps {
  HomePage?: boolean;
  inNavbar?: boolean;
}

const RentalCarSearchForm: FC<RentalCarSearchFormProps> = ({
  HomePage,
  inNavbar = false,
}) => {
  const navigate = useNavigate();
  const {
    dropOffLocation,
    setPickupLocation,
    setDropoffLocation,
    setSearchedPickupLocation,
    setSearchedDropoffLocation,
    pickupLocation,
    setDropoffLocationType,
    setSameDropoffLocation,
    dropoffLocationType,
    startDate,
    endDate,
    callAvailableFleetAPI,
    loadingAvailableFleet,
    hasSelectedStartTime,
    hasSelectedEndTime,
  } = useData();

  // Helper to toggle "Different dropoff location" like the spec image
  const handleDifferentDropoff = () => {
    setDropoffLocationType(!dropoffLocationType);
    setSameDropoffLocation(!dropoffLocationType);
    // Only clear dropoff location, keep pickup location intact
    setDropoffLocation(null);
    setSearchedDropoffLocation("");
  };

  const handleMobileSearch = () => {
    if (pickupLocation === null) {
      toast.error("Please enter Pick up location");
    } else if (dropOffLocation === null && dropoffLocationType === false) {
      toast.error("Please enter Return location");
    } else if (startDate === null && endDate === null) {
      toast.error("Please enter start and end date");
    } else if (!hasSelectedStartTime || !hasSelectedEndTime) {
      toast.error("Please select pickup and return times");
    } else if (pickupLocation !== null) {
      callAvailableFleetAPI(navigate);
    }
  };

  const isDdropOffdifferent = dropoffLocationType === false;

  return (
    <form className="z-30 relative lg:p-3 p-8 rounded-[15px] bg-[#000000] px-4">
      {/* Desktop Layout */}
      <div className="hidden lg:block">
        <div
          className={`relative flex flex-wrap lg:flex-nowrap px-4 ${
            HomePage ? "items-end" : "items-start justify-center"
          } gap-4`}
        >
          {/* Locations row as a grid; no outer label to keep heights consistent */}
          <div className="flex flex-col lg:min-w-[260px] xl:min-w-[370px]">
            <div
              className={`grid grid-cols-1 ${
                isDdropOffdifferent ? "lg:grid-cols-2" : ""
              } gap-2`}
            >
              <div className="flex flex-col">
                {HomePage && (
                  <div className="text-xs text-neutral-300 mb-2 whitespace-nowrap">
                    Pickup Location
                  </div>
                )}
                <LocationInput
                  placeHolder={HomePage ? "Choose Location" : "Pickup Location"}
                  desc="Pickup Location"
                  sameDropOffLocation={dropoffLocationType === false}
                  className="w-full"
                  variant="box"
                  hideIconAtLg={inNavbar}
                />
                {!HomePage && (
                  <button
                    type="button"
                    onClick={handleDifferentDropoff}
                    className="text-[12px] mt-2 text-neutral-300 hover:text-white text-left"
                  >
                    {!isDdropOffdifferent
                      ? " + Different Return Location"
                      : " - Same Return Location"}
                  </button>
                )}
              </div>

              {isDdropOffdifferent && (
                <div className="flex flex-col">
                  {HomePage && (
                    <div className="text-xs text-neutral-300 mb-2 whitespace-nowrap">
                      Return Location
                    </div>
                  )}
                  <DropOffLocationInput
                    placeHolder={
                      HomePage ? "Choose Location" : "Return Location"
                    }
                    desc="Return Location"
                    className="w-full"
                    variant="box"
                    hideIconAtLg={inNavbar}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col ">
            {HomePage && (
              <div className="text-xs text-neutral-300 mb-2 whitespace-nowrap">
                Pickup Date &amp; Time
              </div>
            )}
            <div className="flex flex-row space-x-0">
              <RentalCarDatesRangeInput
                variant="pickup"
                pickupPlaceholder={HomePage ? "Add Date" : "Pickup Date"}
                dropoffPlaceholder={HomePage ? "Add Date" : "Return Date"}
                hideLabel
                segmentClassName="rounded-r-none "
                searchBar={true}
                home={true}
                hideIconAtLg={inNavbar}
              />
              <RentalCarTimesRangeInput
                dropOffLocationType={dropoffLocationType}
                variant="pickup"
                hideLabel
                segmentClassName="rounded-l-none -ml-px "
                searchBar={true}
                pickupPlaceholder={HomePage ? "Add Time" : "Pickup Time"}
                dropoffPlaceholder={HomePage ? "Add Time" : "Return Time"}
              />
            </div>
          </div>

          {/* Return group: Date + Time in one section */}
          <div className="flex flex-col ">
            {HomePage && (
              <div className="text-xs text-neutral-300 mb-2 whitespace-nowrap">
                Return Date &amp; Time
              </div>
            )}
            <div className="flex items-center space-x-0">
              <RentalCarDatesRangeInput
                variant="dropoff"
                pickupPlaceholder={HomePage ? "Add Date" : "Pickup Date"}
                dropoffPlaceholder={HomePage ? "Add Date" : "Return Date"}
                hideLabel
                segmentClassName="rounded-r-none "
                searchBar={true}
                home={true}
                hideIconAtLg={inNavbar}
              />
              <RentalCarTimesRangeInput
                dropOffLocationType={dropoffLocationType}
                variant="dropoff"
                hideLabel
                segmentClassName="rounded-l-none -ml-px "
                searchBar={true}
                pickupPlaceholder={HomePage ? "Add Time" : "Pickup Time"}
                dropoffPlaceholder={HomePage ? "Add Time" : "Return Time"}
              />
            </div>
          </div>

          <div className="whitespace-nowrap">
            <SearchButton searchBar={true} />
          </div>
        </div>
        {HomePage && (
          <button
            type="button"
            onClick={handleDifferentDropoff}
            className="text-[12px] mt-2 text-neutral-300 hover:text-white text-left px-4"
          >
            {!isDdropOffdifferent
              ? " + Different Return Location"
              : " - Same Return Location"}
          </button>
        )}
      </div>

      {/* Mobile/Tablet Layout */}
      <div className="lg:hidden">
        {/* Pickup Location */}
        <div className="mb-4">
          <div className="text-sm text-neutral-300 font-medium mb-2">
            Pickup Location
          </div>
          <LocationInput
            placeHolder="Choose Location"
            sameDropOffLocation={dropoffLocationType === false}
            className="w-full"
            variant="box"
          />
        </div>

        {/* Return Location */}
        {isDdropOffdifferent && (
          <div className="mb-4">
            <div className="text-sm text-neutral-300 font-medium mb-2">
              Return Location
            </div>
            <DropOffLocationInput
              placeHolder="Choose Location"
              className="w-full"
              variant="box"
            />
          </div>
        )}

        {/* Toggle dropoff link */}
        <button
          type="button"
          onClick={handleDifferentDropoff}
          className="text-sm text-neutral-400 hover:text-white text-left mb-4 block"
        >
          {!isDdropOffdifferent
            ? "+ Different Return Location"
            : "- Same Return Location"}
        </button>

        {/* Pickup Date & Time */}
        <div className="mb-4">
          <div className="text-sm text-neutral-300 font-medium mb-2">
            Pickup Date & Time
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <RentalCarDatesRangeInput
                pickupPlaceholder={HomePage ? "Add Date" : "Pickup Date"}
                dropoffPlaceholder={HomePage ? "Add Date" : "Return Date"}
                searchBar={true}
                home={true}
                variant="pickup"
                hideLabel
              />
            </div>
            <div className="flex-1">
              <RentalCarTimesRangeInput
                dropOffLocationType={dropoffLocationType}
                variant="pickup"
                hideLabel
                searchBar={true}
                pickupPlaceholder={HomePage ? "Add Time" : "Pickup Time"}
                dropoffPlaceholder={HomePage ? "Add Time" : "Return Time"}
              />
            </div>
          </div>
        </div>

        {/* Return Date & Time */}
        <div className="mb-6">
          <div className="text-sm text-neutral-300 font-medium mb-2">
            Return Date & Time
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <RentalCarDatesRangeInput
                pickupPlaceholder={HomePage ? "Add Date" : "Pickup Date"}
                dropoffPlaceholder={HomePage ? "Add Date" : "Return Date"}
                variant="dropoff"
                home={true}
                searchBar={true}
                hideLabel
              />
            </div>
            <div className="flex-1">
              <RentalCarTimesRangeInput
                dropOffLocationType={dropoffLocationType}
                variant="dropoff"
                hideLabel
                searchBar={true}
                pickupPlaceholder={HomePage ? "Add Time" : "Pickup Time"}
                dropoffPlaceholder={HomePage ? "Add Time" : "Return Time"}
              />
            </div>
          </div>
        </div>

        {/* Mobile Search Button */}
        <button
          type="button"
          onClick={handleMobileSearch}
          disabled={loadingAvailableFleet}
          className="w-full bg-[#B22222] hover:bg-[#9a1d1d] text-white font-semibold py-4 rounded-lg transition-colors flex items-center justify-center"
        >
          {loadingAvailableFleet ? (
            <svg
              aria-hidden="true"
              className="w-6 h-6 text-gray-200 animate-spin fill-white"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          ) : (
            <span className="flex space-x-1">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                />
              </svg>
              <span>View Cars</span>
            </span>
          )}
        </button>
      </div>
    </form>
  );
};

export default RentalCarSearchForm;
